import React from "react";
import { motion } from "framer-motion";

const navLinks = [
  { name: "Home", href: "#home" },
  { name: "About", href: "#about" },
  { name: "Courses", href: "#courses" },
  { name: "Contact", href: "#contact" },
];

function App() {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-blue-200 min-h-screen font-sans">
      {/* Header / Navbar */}
      <nav className="fixed w-full z-50 bg-white bg-opacity-80 backdrop-blur shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.7 }}
            className="text-2xl font-bold text-blue-700 tracking-wide drop-shadow-lg"
          >
            JSM Academy
          </motion.div>
          <div className="space-x-6 hidden md:block">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-blue-700 hover:text-blue-500 font-medium transition-colors duration-200"
              >
                {link.name}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-28 pb-20 flex flex-col items-center justify-center text-center relative overflow-hidden">
        <motion.h1
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1 }}
          className="text-5xl md:text-7xl font-extrabold text-blue-800 mb-6 drop-shadow-lg"
        >
          Unlock Your Potential
        </motion.h1>
        <motion.p
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="text-xl md:text-2xl text-blue-600 mb-8 max-w-2xl"
        >
          Welcome to <span className="font-bold">JSM Academy</span> — Where Excellence in Education Begins. Join our vibrant community and achieve academic success with expert guidance!
        </motion.p>
        <motion.a
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.96 }}
          href="#contact"
          className="inline-block px-8 py-3 bg-blue-700 text-white font-semibold rounded-full shadow-lg hover:bg-blue-800 transition-colors duration-300 text-lg"
        >
          Get Started
        </motion.a>
        {/* Decorative Animated Circles */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.2 }}
          className="absolute -top-20 -left-32 w-72 h-72 bg-blue-200 rounded-full filter blur-2xl opacity-40 z-0"
        />
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6 }}
          className="absolute -bottom-24 -right-36 w-80 h-80 bg-blue-300 rounded-full filter blur-2xl opacity-40 z-0"
        />
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-white bg-opacity-80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-3xl md:text-4xl font-bold text-blue-800 mb-4"
          >
            About Us
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="text-lg text-blue-700 mb-6"
          >
            JSM Academy is dedicated to nurturing young minds and empowering students to excel in their academic journey. Our experienced faculty, innovative learning environment, and personalized approach ensure every student reaches their full potential.
          </motion.p>
          <div className="flex flex-wrap justify-center gap-8 mt-12">
            {[
              { icon: "🎯", title: "Focused Learning" },
              { icon: "💡", title: "Expert Faculty" },
              { icon: "🤝", title: "Personal Attention" },
              { icon: "🏆", title: "Proven Results" },
            ].map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * idx, duration: 0.5 }}
                className="bg-blue-100 rounded-xl shadow-lg px-8 py-6 flex flex-col items-center w-48"
              >
                <span className="text-4xl mb-2">{item.icon}</span>
                <span className="font-semibold text-blue-800">{item.title}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-20 px-4 bg-gradient-to-br from-blue-100 to-blue-200">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-3xl md:text-4xl font-bold text-blue-800 text-center mb-10"
          >
            Our Courses
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                title: "Mathematics",
                desc: "Conceptual clarity and problem-solving from basics to advanced.",
                color: "from-green-400 to-blue-500",
              },
              {
                title: "Science",
                desc: "Hands-on experiments and deep understanding of fundamentals.",
                color: "from-pink-400 to-purple-500",
              },
              {
                title: "English",
                desc: "Comprehensive language skills for academic excellence.",
                color: "from-yellow-400 to-orange-500",
              },
            ].map((course, idx) => (
              <motion.div
                key={course.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * idx, duration: 0.6 }}
                className={`rounded-xl shadow-xl p-8 bg-gradient-to-br ${course.color} text-white flex flex-col items-start`}
              >
                <h3 className="text-2xl font-bold mb-2">{course.title}</h3>
                <p className="mb-4">{course.desc}</p>
                <span className="inline-block mt-auto px-4 py-1 bg-white bg-opacity-20 rounded-full text-sm font-semibold">Learn More</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-white bg-opacity-80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-3xl md:text-4xl font-bold text-blue-800 mb-4"
          >
            Contact Us
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="text-lg text-blue-700 mb-8"
          >
            Ready to join JSM Academy or have questions? Fill out the form below and our team will get in touch!
          </motion.p>
          <form className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="text"
                placeholder="Your Name"
                className="flex-1 px-4 py-3 rounded-lg border border-blue-200 focus:ring-2 focus:ring-blue-400 outline-none"
                required
              />
              <input
                type="email"
                placeholder="Your Email"
                className="flex-1 px-4 py-3 rounded-lg border border-blue-200 focus:ring-2 focus:ring-blue-400 outline-none"
                required
              />
            </div>
            <textarea
              placeholder="Your Message"
              className="w-full px-4 py-3 rounded-lg border border-blue-200 focus:ring-2 focus:ring-blue-400 outline-none"
              rows={4}
              required
            ></textarea>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              type="submit"
              className="w-full px-8 py-3 bg-blue-700 text-white font-semibold rounded-full shadow-lg hover:bg-blue-800 transition-colors duration-300 text-lg"
            >
              Send Message
            </motion.button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-blue-800 text-white text-center mt-10">
        <span className="font-semibold">© {new Date().getFullYear()} JSM Academy. All rights reserved.</span>
      </footer>
    </div>
  );
}

export default App;
